import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  addContractor,
  calculateCurrentDebt,
  editContractor,
  editContractorSetting,
  getContractorBasicInfo,
  getContractorBasicInfoToEdit,
  getContractorCategories,
  searchContractorInBIR,
} from "@/requests/administration/contractors/contractorBasicInfoAdministration";
import {
  ContractorBasicInfoResponse,
  ContractorSetting,
} from "@/models/api/administration/contractors/ContractorBasicInfoResponse";
import { EditContractor } from "@/models/api/administration/contractors/EditContractor";
import { AddContractorBody } from "@/models/api/administration/contractors/AddContractorBody";
import SelectItem from "@/models/components/inputs/selectItem";

export interface AdministrationContractorBasicInfoStateInterface {
  isEditContractorDialog: boolean;
  isAddContractorDialog: boolean;
  contractorBasicInfo: ContractorBasicInfoResponse;
  contractorBasicInfoToEdit: EditContractor;
  contractorCategories: Array<SelectItem>;
}

export const AdministrationContractorBasicInfoState: AdministrationContractorBasicInfoStateInterface = {
  isEditContractorDialog: false,
  isAddContractorDialog: false,
  contractorBasicInfo: new ContractorBasicInfoResponse(),
  contractorBasicInfoToEdit: new EditContractor(),
  contractorCategories: [],
};

const AdministrationContractorBasicInfoModule = defineModule({
  state: AdministrationContractorBasicInfoState as AdministrationContractorBasicInfoStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setContractorBasicInfo(state, param: ContractorBasicInfoResponse) {
      state.contractorBasicInfo = param;
    },
    setContractorCurrentDebt(state, param: number) {
      if (state.contractorBasicInfo !== null) state.contractorBasicInfo.contractorSetting.currentDebt = param;
    },
    setContractorSetting(state, param: ContractorSetting) {
      if (state.contractorBasicInfo !== null) state.contractorBasicInfo.contractorSetting = param;
    },
    setContractorBasicInfoToEdit(state, param: EditContractor) {
      state.contractorBasicInfoToEdit = param;
    },
    setIsEditContractorDialog(state, param: boolean) {
      state.isEditContractorDialog = param;
    },
    setIsAddContractorDialog(state, param: boolean) {
      state.isAddContractorDialog = param;
    },
    setContractorCategories(state, param: Array<SelectItem>) {
      state.contractorCategories = param;
    },
  },
  actions: {
    async downloadContractorBasicInfo(context) {
      const { commit, rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const contractorBasicInfo = await getContractorBasicInfo(
          rootState.administration.contractors.list.selectedContractorId as string
        );
        commit.setContractorBasicInfo(contractorBasicInfo);
      }
    },
    async calculateContractorCurrentDebt(context) {
      const { commit, rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const contractorCurrentDebt = await calculateCurrentDebt(
          rootState.administration.contractors.list.selectedContractorId as string,
          rootState.administration.contractors.basicInfo.contractorBasicInfo?.contractorSetting.debtLimit as number
        );
        commit.setContractorCurrentDebt(contractorCurrentDebt);
      }
    },
    async saveContractorSetting(context) {
      const { rootState, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await editContractorSetting(
          rootState.administration.contractors.list.selectedContractorId as string,
          rootState.administration.contractors.basicInfo.contractorBasicInfo?.contractorSetting as ContractorSetting
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async editContractorBasicInfo(context, contractorBasicInfo: EditContractor) {
      const { rootState, rootDispatch, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await editContractor(
          rootState.administration.contractors.list.selectedContractorId as string,
          contractorBasicInfo
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadContractorBasicInfo();
      }
    },
    async openEditContractorDialog(context, contractorId: string) {
      const { commit } = _moduleActionContext(context);
      const response = await getContractorBasicInfoToEdit(contractorId);
      const model = new EditContractor();
      model.name = response.name;
      model.surname = response.surname ?? "";
      model.postalCode = response.postalCode;
      model.streetAndNumber = response.streetAndNumber;
      model.email = response.email;
      model.phoneNumber = response.phoneNumber;
      model.contractorTypes = response.contractorTypes;
      model.contractorCategories = response.contractorCategories;
      model.shortName = response.shortName;
      model.nationalBusinessRegistryNumber = response.nationalBusinessRegistryNumber;
      model.tin = response.tin;
      model.countryTypeTIN = response.countryTypeTIN;
      model.city = response.city;
      model.country = response.country ?? "";
      model.isIndividual = response.isIndividual;
      model.isVatGroupMember = response.isVatGroupMember;
      model.isLocalGovernmentUnit = response.isLocalGovernmentUnit;
      model.recipientStatusId = response.recipientStatusId;
      commit.setContractorBasicInfoToEdit(model);
      commit.setIsEditContractorDialog(true);
    },
    async searchContractorInBIR(context, tinToSearch: string) {
      return searchContractorInBIR(tinToSearch);
    },
    async addContractor(context, newContractor: AddContractorBody) {
      const { rootDispatch } = _moduleActionContext(context);
      await addContractor(newContractor);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async getContractorCategories(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getContractorCategories();
      commit.setContractorCategories(response);
    },
  },
});

export default AdministrationContractorBasicInfoModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorBasicInfoModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorBasicInfoModule);
